import { apiRequest, apiRequestPage } from './client';

/**
 * Clients for Visitors (FEAT-008), Global Search (FEAT-015), the Dashboard
 * (FEAT-003) and the Company Report (FEAT-021).
 */

export interface Visitor {
  id: string;
  name: string;
  phone: string | null;
  host_company_id: string;
  host_company_name: string;
  host_employee_id: string | null;
  host_name: string;
  reason: string | null;
  date: string;
  arrived_at: string;
  departed_at: string | null;
  status: 'onsite' | 'left';
}

export interface VisitorFilters {
  q?: string;
  date?: string;
  from?: string;
  to?: string;
  company?: string;
}

export async function listVisitors(
  params: { page: number; pageSize: number } & VisitorFilters,
): Promise<{ items: Visitor[]; total: number; onsite: number }> {
  const { items, meta } = await apiRequestPage<Visitor>('/visitors', {
    query: {
      page: params.page,
      page_size: params.pageSize,
      q: params.q,
      date: params.date,
      from: params.from,
      to: params.to,
      company: params.company,
    },
  });

  /*
   * The on-site figure rides in `meta` beside the pagination, so it comes from
   * the same snapshot as the rows. It deliberately ignores the filters: it
   * answers "how many people are in the building".
   */
  return { items, total: meta.total_items, onsite: (meta as { onsite?: number }).onsite ?? 0 };
}

export function checkInVisitor(body: {
  name: string;
  phone?: string;
  host_company_id: string;
  host_employee_id: string;
  reason?: string;
}): Promise<Visitor> {
  return apiRequest<Visitor>('/visitors', { method: 'POST', body });
}

export function checkOutVisitor(id: string): Promise<Visitor> {
  return apiRequest<Visitor>(`/visitors/${id}/checkout`, { method: 'POST' });
}

/**
 * Corrects when a visitor left — the only editable fact of a visit.
 *
 * Its own route rather than a general update, so nothing else about the record
 * can be changed by sending an extra field.
 */
export function setVisitorDeparture(id: string, departedAt: string): Promise<Visitor> {
  return apiRequest<Visitor>(`/visitors/${id}/departure`, {
    method: 'PUT',
    body: { departed_at: departedAt },
  });
}

export function deleteVisitor(id: string): Promise<void> {
  return apiRequest<void>(`/visitors/${id}`, { method: 'DELETE' });
}

export type SearchModule = 'tickets' | 'bookings' | 'visitors' | 'employees' | 'companies';

export interface SearchHit {
  module: SearchModule;
  id: string;
  title: string;
  subtitle: string;
  path: string;
}

export interface SearchGroup {
  module: SearchModule;
  hits: SearchHit[];
}

export function search(q: string): Promise<SearchGroup[]> {
  return apiRequest<SearchGroup[]>('/search', { query: { q } });
}

export interface Dashboard {
  kpis: { key: string; value: number }[];
  quick_actions: string[];
  recent_activity: { id: string; at: string; actor: string; event: string }[] | null;
  /**
   * The caller's own next meetings. Empty means "nothing coming up" — an
   * answer, and distinct from `recent_activity`'s null, which means "not yours
   * to see".
   */
  upcoming: {
    id: string;
    title: string;
    room_name: string;
    starts_at: string;
    ends_at: string;
    date: string;
  }[];
  degraded: string[];
}

export function getDashboard(): Promise<Dashboard> {
  return apiRequest<Dashboard>('/dashboard');
}

export interface CompanyReport {
  company: {
    id: string;
    name: string;
    cr_number: string;
    website: string | null;
    created_on: string;
    managers: { id: string; name: string; role: string }[];
    comm_contacts: { id: string; name: string; email: string }[];
  };
  kpis: {
    employees: number;
    active: number;
    inactive: number;
    managers: number;
    comm_contacts: number;
    meetings: number;
    tickets: number;
  };
  employees_by_status: { status: string; count: number }[];
  tickets_by_status: { status: string; count: number }[];
  employees: {
    id: string;
    name: string;
    office: string | null;
    phone: string;
    email: string;
    role: string;
    is_comm_contact: boolean;
    status: string;
  }[];
  meetings: {
    id: string;
    title: string;
    room_name: string;
    starts_at: string;
    organizer: string;
  }[];
  tickets: { id: string; subject: string; type: string; status: string }[];
  unavailable: string[];
}

export function getCompanyReport(id: string): Promise<CompanyReport> {
  return apiRequest<CompanyReport>(`/companies/${id}/report`);
}
